import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Drawer,
  Fade,
  Grid,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';

import {
  ISubmission,
  ISubmissionQuestion,
} from '../../../../store/SubmissionSlice';
import { getDiagnosticState } from '../../../../store/DiagnosticSlice';
import { getSubTopicState } from '../../../../store/SubTopicSlice';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import {
  getQuizScoreLabelColor,
  getQuizScoreStatus,
  getUrlConvertedString,
} from '../../../../helper';

const gridSize = 45; // px

type TQuestionListContainerProps = {
  submission: ISubmission;
  selectedQuestion?: ISubmissionQuestion;
  onQuestionClicked: (item: ISubmissionQuestion) => void;
  submitQuiz?: () => void;
  isDiagnostic: boolean;
};

type TQuestionGridItemData = {
  currentItem: ISubmissionQuestion;
  selectedItem?: ISubmissionQuestion;
};

const containerStyles: SxProps = {
  backgroundColor: TP_COLORS.white,
  width: '100%',
  position: 'fixed',
  bottom: 0,
  left: 0,
};

const questionGridContainer: SxProps = {
  display: 'flex',
  padding: '5px',
  overflow: 'auto',
};

const mobileQuestionGridContainer: SxProps = {
  display: 'grid',
  justifyContent: 'center',
  gridTemplateColumns: 'auto auto auto auto auto',
  padding: '5px',
};

const baseQuestionGridItemStyles: SxProps = {
  padding: '5px',
  minWidth: `${gridSize}px`,
  minHeight: `${gridSize}px`,
  maxWidth: `${gridSize}px`,
  maxHeight: `${gridSize}px`,
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: '5px',
};

const questionGridItemDividerStyles: SxProps = {
  borderColor: 'transparent',
  marginTop: '5px',
  borderWidth: '2px',
};

const actionButtonStyles: SxProps = {
  backgroundColor: '#01A79F',
  color: TP_COLORS.white,
  borderRadius: '30px',
  boxShadow: 'rgb(1, 115, 109) 1px 3px 0px',
  width: '190px',
  maxWidth: '100%',
};

const avatarStyles: React.CSSProperties = {
  marginRight: '10px',
  color: TP_COLORS.black,
  backgroundColor: '#E2E8F0',
};

const drawerPaperStyles: SxProps = {
  borderTopLeftRadius: '30px',
  borderTopRightRadius: '30px',
  maxHeight: '80vh',
};

const QuestionListContainer: React.FC<TQuestionListContainerProps> = ({
  submission,
  selectedQuestion,
  onQuestionClicked,
  submitQuiz = undefined,
  isDiagnostic,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));

  // Store
  const diagnosticState = useAppSelector(getDiagnosticState);
  const subTopicState = useAppSelector(getSubTopicState);

  const subTopicData = subTopicState.subTopic;
  const quizData = submission && submission.quiz ? submission.quiz : undefined;
  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const submissionQuestions = submission.questions || [];
  const totalQuestions = submissionQuestions.length;
  const selectedQuestionOrder = (
    (selectedQuestion && selectedQuestion.order ? selectedQuestion.order : 0) +
    1
  )
    .toString()
    .padStart(2, '0');

  const urlConvertedCurriculum = getUrlConvertedString(
    topicData?.curriculum || ''
  );
  const urlConvertedSubject = getUrlConvertedString(topicData?.subject || '');
  const urlConvertedTopic = getUrlConvertedString(topicData?.name || '');
  const urlConvertedSubTopic = getUrlConvertedString(subTopicData?.name || '');

  // States
  const [state, setState] = useState({
    isQuestionListDrawerOpen: false,
  });

  const toggleQuestionListDrawer = () => {
    setState((prevState) => ({
      ...prevState,
      isQuestionListDrawerOpen: !prevState.isQuestionListDrawerOpen,
    }));
  };

  const getQuestionGridItemData = (attributes: TQuestionGridItemData) => {
    const { currentItem, selectedItem } = attributes;
    const orderNumber = currentItem.order + 1;
    const uniqueKey = `question-item-${orderNumber}`;

    let dividerColor: React.CSSProperties['borderColor'] = 'transparent';
    let backgroundColor: React.CSSProperties['background'] = '#E2E8F0';
    let color: React.CSSProperties['color'] = TP_COLORS.black;

    if (selectedItem && currentItem.question === selectedItem.question) {
      dividerColor = '#01A79F';
    }

    if (submission.status === 'completed') {
      if (currentItem.status === 'skipped') {
        backgroundColor = '#FFF5CC';
        color = TP_COLORS.black;
      } else if (currentItem.answer && currentItem.answer.isCorrect === true) {
        backgroundColor = '#D3FCD2';
        color = '#22C55E';
      } else if (currentItem.answer && currentItem.answer.isCorrect === false) {
        backgroundColor = '#FFE9D5';
        color = '#FF5630';
      }

      if (currentItem.isOpenEnded && currentItem.openEnded) {
        if (currentItem.openEnded.score) {
          backgroundColor = '#D3FCD2';
          color = '#22C55E';
        } else if (currentItem.openEnded.score === 0) {
          backgroundColor = '#FFE9D5';
          color = '#FF5630';
        }
      }
    } else if (submission.status === 'pending') {
      if (currentItem.status === 'completed') {
        backgroundColor = '#BDC2CB';
      }
    }

    return {
      orderNumber,
      uniqueKey,
      dividerColor,
      bgColor: backgroundColor,
      textColor: color,
    };
  };

  const renderActionButtons = (styles = actionButtonStyles) => {
    if (submission.status === 'pending') {
      return (
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={styles}
          onClick={submitQuiz}
        >
          Submit
        </Button>
      );
    }

    if (submission.status === 'completed') {
      if (
        isDiagnostic &&
        diagnosticState.diagnostic &&
        diagnosticState.diagnostic._id &&
        params.subtopicId
      ) {
        const link = `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData?._id}/diagnostic-quiz/${diagnosticState.diagnostic._id}`;

        return (
          <Button
            size="large"
            variant="contained"
            color="primary"
            sx={styles}
            onClick={() => navigate(link)}
          >
            See Detailed Report
          </Button>
        );
      }

      const quizScoreStatus = getQuizScoreStatus(
        submission.score,
        submission.totalScore
      );

      const colorData = getQuizScoreLabelColor(quizScoreStatus);

      return (
        <Chip
          color={colorData.chipType}
          label={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <span
                style={{ fontSize: '20px', fontWeight: 600 }}
              >{`${quizScoreStatus}`}</span>
              &nbsp; &nbsp;
              <span style={{ fontSize: '32px', fontWeight: 600 }}>
                {submission.score}/{submission.totalScore}
              </span>
            </span>
          }
          style={{
            borderRadius: '10px',
            minWidth: '170px',
            maxWidth: '100%',
            paddingTop: belowSm ? '20px' : '25px',
            paddingBottom: belowSm ? '20px' : '25px',
          }}
        />
      );
    }

    return null;
  };

  if (belowSm) {
    return (
      <Fade in>
        <Box sx={{ ...containerStyles, padding: '15px' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Card
              elevation={0}
              sx={{ backgroundColor: '#F1F5F9', borderRadius: '30px' }}
            >
              <CardActionArea onClick={toggleQuestionListDrawer}>
                <CardContent style={{ padding: '6px', paddingBottom: '6px' }}>
                  <Stack direction="row" alignItems="center">
                    <Avatar style={avatarStyles}>
                      {selectedQuestionOrder}
                    </Avatar>
                    <GridViewRoundedIcon sx={{ color: '#01A79F' }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
            {renderActionButtons({ ...actionButtonStyles, minWidth: '125px' })}
          </Stack>
          {state.isQuestionListDrawerOpen ? (
            <Drawer
              anchor="bottom"
              open={state.isQuestionListDrawerOpen}
              onClose={toggleQuestionListDrawer}
              PaperProps={{ sx: drawerPaperStyles }}
            >
              <Box role="presentation" style={{ padding: '30px 15px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <Typography textAlign="center" fontWeight={600}>
                    Quiz Navigation (
                    {totalQuestions.toString().padStart(2, '0')})
                  </Typography>
                </div>
                <Box sx={mobileQuestionGridContainer}>
                  {submissionQuestions.map((item, index) => {
                    const {
                      orderNumber,
                      uniqueKey,
                      dividerColor,
                      bgColor,
                      textColor,
                    } = getQuestionGridItemData({
                      currentItem: item,
                      selectedItem: selectedQuestion,
                    });

                    const dividerStyles = {
                      ...questionGridItemDividerStyles,
                      borderColor: dividerColor,
                    };

                    const gridItemStyles = {
                      ...baseQuestionGridItemStyles,
                      backgroundColor: bgColor,
                      color: textColor,
                    };

                    return (
                      <Stack
                        key={uniqueKey}
                        style={{ margin: '0 10px 10px 0' }}
                      >
                        <ButtonBase
                          sx={gridItemStyles}
                          onClick={() => {
                            onQuestionClicked(item);
                            toggleQuestionListDrawer();
                          }}
                        >
                          {orderNumber}
                        </ButtonBase>
                        <Divider sx={dividerStyles} />
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            </Drawer>
          ) : null}
        </Box>
      </Fade>
    );
  }

  return (
    <>
      <Fade in>
        <Box sx={{ ...containerStyles, padding: '15px 0' }}>
          <Container maxWidth="xl">
            <Grid container spacing={2} alignItems="center">
              <Grid item sm={9} md={10} lg={10}>
                <Box sx={questionGridContainer}>
                  {submissionQuestions.map((item, index) => {
                    const {
                      orderNumber,
                      uniqueKey,
                      dividerColor,
                      bgColor,
                      textColor,
                    } = getQuestionGridItemData({
                      currentItem: item,
                      selectedItem: selectedQuestion,
                    });

                    const dividerStyles = {
                      ...questionGridItemDividerStyles,
                      borderColor: dividerColor,
                    };

                    const gridItemStyles: SxProps = {
                      ...baseQuestionGridItemStyles,
                      backgroundColor: bgColor,
                      color: textColor,
                    };

                    return (
                      <Stack key={uniqueKey} style={{ marginRight: '5px' }}>
                        <ButtonBase
                          sx={gridItemStyles}
                          onClick={() => onQuestionClicked(item)}
                        >
                          {orderNumber}
                        </ButtonBase>
                        <Divider sx={dividerStyles} />
                      </Stack>
                    );
                  })}
                </Box>
              </Grid>
              <Grid item sm={3} md={2} lg={2}>
                <Stack alignItems="flex-end">{renderActionButtons()}</Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Fade>
    </>
  );
};

export default QuestionListContainer;
